import { useState, useEffect, useRef, useCallback } from "react";

interface WebSocketProps<T> {
  url: string;
  onMessageReceived?: (data: MessageEvent<T>) => void;
  disableInitialConnection?: boolean;
}

interface WebSocketReturn<T> {
  isConnected: boolean;
  sendData: (data: T) => void;
  error: Error | null;
  reconnect: () => void;
  closeConnection: () => void;
}

/**
 * @param url - The URL of the WebSocket server.
 * @param onMessageReceived - A callback function that is called when a message is received from the WebSocket server.
 * @param disableInitialConnection - A boolean that indicates whether to disable the initial connection to the WebSocket server.
 * @returns An object with the following properties:
 * - isConnected: A boolean that indicates whether the WebSocket connection is established.
 * - sendData: A function that can be used to send data to the WebSocket server.
 * - error: An error object if the WebSocket connection encounters an error.
 * - reconnect: A function that can be used to reconnect to the WebSocket server.
 * - closeConnection: A function that can be used to close the WebSocket connection.
 *
 * @author - [Rajkumar Rathod]
 */
const useWebSocket = <T>({
  url,
  onMessageReceived,
  disableInitialConnection = false,
}: WebSocketProps<T>): WebSocketReturn<T> => {
  const handleMessageReceived = useCallback((event: MessageEvent<T>) => {
    onMessageReceived?.(event);
  }, []);

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const ws = useRef<WebSocket | null>(null);

  useEffect(() => {
    /** If the initial connection is disabled then don't establish the connection. */
    if (!disableInitialConnection) {
      ws.current = new WebSocket(url);

      /** Events to handle the WebSocket connection established.*/
      ws.current.onopen = () => setIsConnected(true);

      /** Events to handle the WebSocket message received.*/
      ws.current.onmessage = (event) => handleMessageReceived(event);

      /** Events to handle the WebSocket connection closed.*/
      ws.current.onclose = () => setIsConnected(false);

      /** Events to handle the WebSocket connection error.*/
      ws.current.onerror = (error) => {
        setError(new Error(JSON.stringify(error)));
      };
    }

    /* Clean Up */
    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [url]);

  /**
   * @description - A function to send data to the WebSocket server.
   * @param data - The data to send to the WebSocket server.
   */
  const sendData = (data: any) => {
    /** Frames sent before the socket opens are dropped, not queued. */
    if (ws.current && ws.current.readyState === WebSocket.OPEN)
      ws.current.send(data);
  };

  /** close previous connection and create a new one */
  const reconnect = useCallback(() => {
    if (ws.current) ws.current.close();

    ws.current = new WebSocket(url);
    /** open the new connection */
    ws.current.onopen = () => setIsConnected(true);

    /** new connection message event received handler */
    ws.current.onmessage = (event) => handleMessageReceived(event);
  }, [url]);

  const closeConnection = useCallback(() => {
    if (ws.current) ws.current.close();
  }, []);

  return { isConnected, sendData, error, reconnect, closeConnection };
};

export default useWebSocket;
